import { InvoiceChargeTypeEnum, InvoiceStatusEnum } from '@/models/gen/graphql';
import { MONTH_END, MONTH_START } from '@/constants';

import ClientDropdown from '@/components/ClientDropdown';
import EnumDropdown from '@/components/EnumDropdown';
import Filters from '@/components/Filters';
import FormButton from '@/components/FormButton';
import FormField from '@/components/FormField';
import { onEnter } from '@/utils';
import useForm from '@/hooks/useForm';
import { useInvoice } from '../InvoicePreview';

export type InvoiceHistoryFiltersProps = {
  selected: string[];
  onSubmit: (filters: InvoiceHistoryFiltersState) => void;
  onArchive: () => void;
  onSearch: (search: string) => void;
};
export interface InvoiceHistoryFiltersState {
  from: typeof MONTH_START;
  to: typeof MONTH_END;
  search: string;
  payerProviderId: string;
  status: string[];
  chargeType: string[];
}
const initInvoiceHistoryFiltersState: InvoiceHistoryFiltersState = {
  from: MONTH_START,
  to: MONTH_END,
  search: '',
  payerProviderId: null,
  status: [InvoiceStatusEnum.Active],
  chargeType: null,
};

export const InvoiceHistoryFilters = ({ selected = [], onSubmit, onArchive, onSearch }: InvoiceHistoryFiltersProps): JSX.Element => {
  const [filters, , setFilters] = useForm(initInvoiceHistoryFiltersState);

  const handleSearch = (search = ''): void => {
    setFilters((current: InvoiceHistoryFiltersState): InvoiceHistoryFiltersState => ({ ...current, search }));
    onSearch(search);
  };
  const setInvoiceState = useInvoice(({ setState }) => setState);

  return (
    <Filters
      name="invoiceHistoryTable"
      value={filters}
      onSubmit={onSubmit}
      onReset={(): void => {
        setFilters(initInvoiceHistoryFiltersState);
        handleSearch();
      }}
      submitOnMount
      primary={({ values, onChange }): JSX.Element => {
        const { from, to, payerProviderId, status, chargeType } = values;
        return (
          <>
            <FormField name="daterange" type="daterange" value={[from, to]} onChange={onChange.dateRange('from', 'to')} condensed />
            <ClientDropdown
              name="payerProviderId"
              onChange={(value) => onChange({ target: { name: 'payerProviderId', value } })}
              value={payerProviderId}
            />
            <EnumDropdown
              name="status"
              value={status}
              onChange={(value) => onChange({ target: { name: 'status', value } })}
              options={{ locale: { 'Select...': 'Status' } }}
              enum={InvoiceStatusEnum}
            />
            <EnumDropdown
              name="chargeType"
              value={chargeType}
              onChange={(value) => onChange({ target: { name: 'chargeType', value } })}
              options={{ locale: { 'Select...': 'Invoice Type', Trip: 'Invoice', CustomCharge: 'Custom Invoice' } }}
              enum={InvoiceChargeTypeEnum}
            />
          </>
        );
      }}
      controls={({ values, onChange }): JSX.Element => {
        const { search } = values;
        return (
          <>
            <FormField
              prepend={<i className="sv sv-magnifier fs-4" />}
              id={'filters-searchbar'}
              name="search"
              value={search || ''}
              onChange={onChange}
              onBlur={() => handleSearch(search)}
              onKeyDown={onEnter(() => handleSearch(search))}
              placeholder="Search"
              condensed
              inline
            />
          </>
        );
      }}
      alternate={(): JSX.Element => {
        return (
          <>
            <FormButton
              name="NEW_INVOICE"
              icon={<i className="sv sv-plus-square {font-size:1.5rem;}" />}
              variant="outline-gray"
              to="/invoices/0"
              onClick={() => {
                setInvoiceState((state) => ({ ...state, isCustom: false }));
              }}
            >
              Create Invoice
            </FormButton>
            <FormButton
              name="CUSTOM_INVOICE"
              icon={<i className="sv sv-wallet {font-size:1.5rem;}" />}
              variant="outline-gray"
              to="/invoices/0"
              onClick={() => {
                setInvoiceState((state) => ({ ...state, isCustom: true }));
              }}
            >
              Create Custom Invoice
            </FormButton>
            <FormButton
              name="ARCHIVE_INVOICES"
              icon={<i className="sv sv-box {font-size:1.5rem;}" />}
              feedback={`Archive Invoice${selected?.length > 1 ? 's' : ''}`}
              variant="outline-gray"
              onClick={onArchive}
              disabled={selected?.length === 0}
            />
          </>
        );
      }}
    />
  );
};
